import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import type {ActivityTab_activity$data, ActivityTab_activity$key} from './__generated__/ActivityTab_activity.graphql';
import {TicketStatusBadge} from './TicketStatusBadge';
import type {TicketStatus} from '../types/ticket';
import {htmlToPreviewText} from '../utils/htmlToPreviewText';
import {resolveMentions} from '../utils/resolveMentions';

// Dot palette keyed by activity kind. Ports the status/brand/subtle token pairs
// the dot previously read via bg-[var(--fd-*)] arbitrary classes.
type DotVariant = 'email' | 'comment' | 'status' | 'time' | 'other';
const DOT_COLORS: Record<DotVariant, {bg: string; text: string}> = {
	email: {bg: '#dbeafe', text: '#1e40af'}, // status-open
	comment: {bg: '#dcfce7', text: '#166534'}, // status-resolved
	status: {bg: '#fef9c3', text: '#854d0e'}, // status-in-progress
	time: {bg: '#f3e8ff', text: '#6e0feb'}, // brand-50 / interactive-primary
	other: {bg: '#f1f5f9', text: '#6b7280'}, // bg-subtle / text-secondary
};

const ActivityTabFragment = graphql`
	fragment ActivityTab_activity on ActivityEvent @relay(plural: true) {
		__typename
		... on MessageActivity {
			id
			createdAt
			body
			authorType
			direction
			author {
				fullName
			}
		}
		... on InternalCommentActivity {
			id
			createdAt
			body
			author {
				fullName
			}
			mentionedUsers {
				id
				fullName
			}
		}
		... on EventActivity {
			id
			createdAt
			eventType
			actor {
				fullName
			}
			oldValue
			newValue
		}
		... on AttachmentActivity {
			id
			createdAt
			filename
		}
	}
`;

type FilterId = 'all' | 'email' | 'comment' | 'status';
type EntryFilterId = 'email' | 'comment' | 'status' | 'other';

interface Props {
	entries: ActivityTab_activity$key;
}

type Entry = ActivityTab_activity$data[number];
type ConcreteEntry = Exclude<Entry, {readonly __typename: '%other'}>;

interface NormalisedEntry {
	id: string;
	createdAt: string;
	filterId: EntryFilterId;
	actorName: string;
	actionText: string;
	body: string | null;
	statusFromTo: {from: string | null; to: string | null} | null;
	dotVariant: DotVariant;
}

const KNOWN_TICKET_STATUSES: ReadonlySet<TicketStatus> = new Set<TicketStatus>([
	'open',
	'in_progress',
	'waiting_on_customer',
	'resolved',
	'closed',
]);

function isTicketStatus(value: string | null | undefined): value is TicketStatus {
	return value != null && KNOWN_TICKET_STATUSES.has(value as TicketStatus);
}

function formatTimestamp(iso: string): string {
	return new Date(iso).toLocaleString();
}

function normalise(entry: ConcreteEntry): NormalisedEntry | null {
	switch (entry.__typename) {
		case 'MessageActivity': {
			const isOutbound = entry.direction === 'outbound' || entry.authorType === 'agent';
			const authorName = entry.author ? entry.author.fullName ?? 'Deleted user' : isOutbound ? 'Agent' : 'Requester';
			return {
				id: entry.id,
				createdAt: entry.createdAt as string,
				filterId: 'email',
				actorName: authorName,
				actionText: isOutbound ? 'sent an email' : 'received an email',
				body: htmlToPreviewText(entry.body),
				statusFromTo: null,
				dotVariant: 'email',
			};
		}
		case 'InternalCommentActivity': {
			const names = Object.fromEntries((entry.mentionedUsers ?? []).map(u => [u.id, u.fullName ?? 'Deleted user']));
			return {
				id: entry.id,
				createdAt: entry.createdAt as string,
				filterId: 'comment',
				actorName: entry.author ? entry.author.fullName ?? 'Deleted user' : 'Agent',
				actionText: 'added an internal note',
				body: htmlToPreviewText(resolveMentions(entry.body, names)),
				statusFromTo: null,
				dotVariant: 'comment',
			};
		}
		case 'EventActivity': {
			const actorName = entry.actor ? entry.actor.fullName ?? 'Deleted user' : 'System';
			if (entry.eventType === 'status_change') {
				return {
					id: entry.id,
					createdAt: entry.createdAt as string,
					filterId: 'status',
					actorName,
					actionText: 'changed status',
					body: null,
					statusFromTo: {from: entry.oldValue ?? null, to: entry.newValue ?? null},
					dotVariant: 'status',
				};
			}
			if (entry.eventType === 'time_entry') {
				return {
					id: entry.id,
					createdAt: entry.createdAt as string,
					filterId: 'other',
					actorName,
					actionText: 'logged time',
					body: null,
					statusFromTo: null,
					dotVariant: 'time',
				};
			}
			return {
				id: entry.id,
				createdAt: entry.createdAt as string,
				filterId: 'other',
				actorName,
				actionText: entry.eventType.replace(/_/g, ' '),
				body: null,
				statusFromTo: null,
				dotVariant: 'other',
			};
		}
		case 'AttachmentActivity':
			return {
				id: entry.id,
				createdAt: entry.createdAt as string,
				filterId: 'other',
				actorName: 'System',
				actionText: `attached ${entry.filename}`,
				body: null,
				statusFromTo: null,
				dotVariant: 'other',
			};
		default:
			return null;
	}
}

const FILTER_DEFS: {id: FilterId; label: string}[] = [
	{id: 'all', label: 'All'},
	{id: 'email', label: 'Email'},
	{id: 'comment', label: 'Comment'},
	{id: 'status', label: 'Status'},
];

const TabContainer = styled.div`
	> * + * {
		margin-top: 16px;
	}
`;

const Header = styled.header`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Title = styled.h3`
	margin: 0;
	font: ${p => p.theme.ticketing.font.subheading1};
	letter-spacing: 0.15px;
	color: #0f172a;
`;

const FilterGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const FilterButton = styled.button<{$active: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	padding: 4px 12px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.15s;
	border: 1px solid ${p => (p.$active ? p.theme.ticketing.color.brand100 : '#e2e8f0')};
	background: ${p => (p.$active ? p.theme.ticketing.color.brand50 : '#ffffff')};
	color: ${p => (p.$active ? p.theme.ticketing.color.interactivePrimary : '#475569')};
	&:hover {
		background: ${p => (p.$active ? p.theme.ticketing.color.brand50 : '#f8fafc')};
	}
`;

const FilterCount = styled.span<{$active: boolean}>`
	border-radius: ${p => p.theme.ticketing.radius.full};
	padding: 2px 6px;
	font: ${p => p.theme.ticketing.font.overline};
	font-size: 10px;
	font-weight: 600;
	background: ${p => (p.$active ? p.theme.ticketing.color.brand100 : '#f1f5f9')};
	color: ${p => (p.$active ? p.theme.ticketing.color.interactivePrimary : '#64748b')};
`;

const EmptyText = styled.p`
	margin: 0;
	font: ${p => p.theme.ticketing.font.body2};
	color: #64748b;
`;

const EntryList = styled.ol`
	margin: 0;
	padding: 0;
	list-style: none;
	> li + li {
		margin-top: 12px;
	}
`;

const Badge = styled.span<{$bg: string; $color: string}>`
	display: inline-flex;
	align-items: center;
	border-radius: 4px;
	padding: 2px 6px;
	font: ${p => p.theme.ticketing.font.overline};
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	background: ${p => p.$bg};
	color: ${p => p.$color};
`;

const EntryRow = styled.li`
	display: flex;
	gap: 12px;
`;

const DotColumn = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Dot = styled.span<{$variant: DotVariant}>`
	display: flex;
	height: 32px;
	width: 32px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	background: ${p => DOT_COLORS[p.$variant].bg};
	color: ${p => DOT_COLORS[p.$variant].text};
`;

const Connector = styled.span`
	margin-top: 4px;
	width: 2px;
	flex: 1 1 0%;
	background: #e2e8f0;
`;

const EntryBody = styled.div`
	min-width: 0;
	flex: 1 1 0%;
	padding-bottom: 16px;
`;

const EntryMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	column-gap: 8px;
`;

const ActorName = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 600;
	color: #0f172a;
`;

const ActionText = styled.span`
	font: ${p => p.theme.ticketing.font.body2};
	color: #475569;
`;

const MetaTimestamp = styled.span`
	margin-left: auto;
	font: ${p => p.theme.ticketing.font.body3};
	color: #94a3b8;
`;

const Detail = styled.div`
	margin-top: 8px;
	border-radius: 6px;
	border: 1px solid #f1f5f9;
	background: #f8fafc;
	padding: 12px 16px;
`;

const DetailBody = styled.p`
	margin: 0;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #334155;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;

const ShowFullButton = styled.button`
	margin-top: 4px;
	border: 0;
	background: transparent;
	padding: 0;
	cursor: pointer;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #4f47d6;
	&:hover {
		color: #3d36b3;
	}
`;

const StatusRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const StatusText = styled.span<{$muted?: boolean}>`
	font: ${p => p.theme.ticketing.font.body2};
	color: ${p => (p.$muted ? '#94a3b8' : '#64748b')};
`;

export function ActivityTab({entries}: Props) {
	const data = useFragment<ActivityTab_activity$key>(ActivityTabFragment, entries);

	const [activeFilter, setActiveFilter] = useState<FilterId>('all');

	const items = useMemo(() => {
		const result: NormalisedEntry[] = [];
		for (const entry of data) {
			if (entry.__typename === '%other') continue;
			const item = normalise(entry as ConcreteEntry);
			if (item) result.push(item);
		}
		return result;
	}, [data]);

	const visible = useMemo(() => {
		const filtered = items.filter(item => {
			if (activeFilter !== 'all' && item.filterId !== activeFilter) return false;
			return true;
		});
		filtered.sort((a, b) => {
			const av = new Date(a.createdAt).getTime();
			const bv = new Date(b.createdAt).getTime();
			return bv - av;
		});
		return filtered;
	}, [items, activeFilter]);

	const filterCounts = useMemo(() => {
		const counts: Record<FilterId, number> = {all: items.length, email: 0, comment: 0, status: 0};
		for (const item of items) {
			if (item.filterId === 'email') counts.email++;
			else if (item.filterId === 'comment') counts.comment++;
			else if (item.filterId === 'status') counts.status++;
		}
		return counts;
	}, [items]);

	return (
		<TabContainer data-testid="activity-tab">
			<Header>
				<Title>Activity feed</Title>
				<FilterGroup>
					{FILTER_DEFS.map(def => {
						const isActive = activeFilter === def.id;
						const count = filterCounts[def.id];
						return (
							<FilterButton
								key={def.id}
								type="button"
								$active={isActive}
								aria-pressed={isActive}
								onClick={() => setActiveFilter(def.id)}
							>
								{def.label}
								<FilterCount $active={isActive}>{count}</FilterCount>
							</FilterButton>
						);
					})}
				</FilterGroup>
			</Header>

			{visible.length === 0 ? (
				<EmptyText>
					{items.length === 0 ? 'No activity yet on this ticket.' : 'No activity matching your filters.'}
				</EmptyText>
			) : (
				<EntryList data-testid="activity-tab-list">
					{visible.map((item, i) => (
						<ActivityEntryRow key={item.id} entry={item} isLast={i === visible.length - 1} />
					))}
				</EntryList>
			)}
		</TabContainer>
	);
}

function BadgeForEntry({entry}: {entry: NormalisedEntry}) {
	if (entry.filterId === 'email') {
		const isReceived = entry.actionText.includes('received');
		return isReceived ? (
			<Badge $bg="#dcfce7" $color="#15803d">
				EMAIL RECEIVED
			</Badge>
		) : (
			<Badge $bg="#dbeafe" $color="#1d4ed8">
				EMAIL SENT
			</Badge>
		);
	}
	if (entry.filterId === 'status') {
		return (
			<Badge $bg="#fef3c7" $color="#b45309">
				Status
			</Badge>
		);
	}
	return null;
}

function ActivityEntryRow({entry, isLast}: {entry: NormalisedEntry; isLast: boolean}) {
	return (
		<EntryRow id={entry.id} data-testid="activity-entry">
			<DotColumn>
				<Dot aria-hidden="true" data-testid="activity-dot" $variant={entry.dotVariant}>
					{entry.actorName.charAt(0).toUpperCase()}
				</Dot>
				{!isLast && <Connector aria-hidden="true" data-testid="activity-connector" />}
			</DotColumn>
			<EntryBody>
				<EntryMeta>
					<ActorName>{entry.actorName}</ActorName>
					<BadgeForEntry entry={entry} />
					<ActionText>{entry.actionText}</ActionText>
					<MetaTimestamp>{formatTimestamp(entry.createdAt)}</MetaTimestamp>
				</EntryMeta>
				{(entry.body !== null || entry.statusFromTo !== null) && <DetailBlock entry={entry} />}
			</EntryBody>
		</EntryRow>
	);
}

function DetailBlock({entry}: {entry: NormalisedEntry}) {
	return (
		<Detail data-testid="activity-detail">
			{entry.statusFromTo ? (
				<StatusFromTo from={entry.statusFromTo.from} to={entry.statusFromTo.to} />
			) : (
				<>
					<DetailBody>{entry.body}</DetailBody>
					<ShowFullButton type="button">Show full email</ShowFullButton>
				</>
			)}
		</Detail>
	);
}

function StatusFromTo({from, to}: {from: string | null; to: string | null}) {
	return (
		<StatusRow>
			{isTicketStatus(from) ? <TicketStatusBadge status={from} /> : <StatusText>{from ?? '—'}</StatusText>}
			<StatusText $muted aria-hidden="true">
				→
			</StatusText>
			{isTicketStatus(to) ? <TicketStatusBadge status={to} /> : <StatusText>{to ?? '—'}</StatusText>}
		</StatusRow>
	);
}
